/**
 * AdminWhatsApp — `/admin/whatsapp` (iter100.253)
 *
 * Command center for the Meta WhatsApp Cloud API integration.
 * Sections:
 *   1. Mode banner (MOCK vs LIVE)
 *   2. KPI strip: sent today / this month / errors / opt-in rate
 *   3. "Run smart trigger now" button + last-run info
 *   4. Reminder send log (paginated table, most recent first)
 *   5. Test-send dialog (any E.164, any template) — for smoke testing
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Play, PaperPlaneTilt, CheckCircle, XCircle,
    ArrowsClockwise, User, Clock, CalendarBlank,
} from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";

const fmtDateTime = (s) =>
    s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_STYLES = {
    sent:      "bg-emerald-100 text-emerald-700 border-emerald-200",
    delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
    read:      "bg-sky-100 text-sky-700 border-sky-200",
    error:     "bg-rose-100 text-rose-700 border-rose-200",
};

export default function AdminWhatsApp() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningPass, setRunningPass] = useState(false);
    const [lastPass, setLastPass] = useState(null);
    const [testOpen, setTestOpen] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/whatsapp/stats"),
            api.get("/admin/whatsapp/reminders?limit=50"),
            // iter100.268 — Meta template approval status pill.
            // Fails soft — if the token expired or Meta is unreachable,
            // the templates list stays empty and the section renders
            // "Could not fetch" so the rest of the page still loads.
            api.get("/admin/whatsapp/templates").catch(() => ({ data: { items: [] } })),
        ]).then(([s, r, t]) => {
            setStats(s.data);
            setItems(r.data?.items || []);
            setTemplates(t.data?.items || []);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunningPass(true);
        try {
            const r = await api.post("/admin/whatsapp/reminders/run-now");
            setLastPass(r.data);
            toast.success(`Pass complete — ${r.data.sent} sent, ${r.data.candidates} candidates`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to run pass");
        } finally {
            setRunningPass(false);
        }
    };

    const isLive = stats?.mode === "live";

    return (
        <div className="space-y-6" data-testid="admin-whatsapp-page">
            {/* Header — canonical grammar (iter101.32) */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin · WhatsApp</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">WhatsApp Reminders</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Meta Cloud API — hourly wear-log reminders for streak-breaks. Utility templates, opt-in only.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => setTestOpen(true)} data-testid="whatsapp-test-send-btn">
                        <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                        Test send
                    </Button>
                    <Button size="sm" className="rounded-full" onClick={runNow} disabled={runningPass} data-testid="whatsapp-run-now-btn">
                        <Play size={14} weight="fill" className="mr-1.5" />
                        {runningPass ? "Running…" : "Run smart trigger"}
                    </Button>
                </div>
            </div>

            {/* KPI strip — canonical StatTile shape */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="whatsapp-kpi-strip">
                <StatTile label="Sent today"        value={stats?.sent_today ?? 0}                        Icon={PaperPlaneTilt} tone="primary" testid="whatsapp-kpi-today" />
                <StatTile label="Sent this month"   value={stats?.sent_this_month ?? 0}                   Icon={CalendarBlank}  tone="emerald" testid="whatsapp-kpi-month" />
                <StatTile label="Errors (total)"    value={stats?.errors ?? 0}                            Icon={XCircle}        tone={stats?.errors ? "rose" : "slate"} testid="whatsapp-kpi-errors" />
                <StatTile
                    label="Opted-in patients"
                    value={`${stats?.opted_in_patients ?? 0} / ${stats?.total_patients ?? 0}`}
                    Icon={User}
                    tone="violet"
                    testid="whatsapp-kpi-opt-in"
                />
            </div>

            {/* Mode banner */}
            <div className={`rounded-xl border px-4 py-2.5 text-sm flex items-center gap-2 ${
                isLive
                    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                    : "bg-amber-50 border-amber-200 text-amber-900"
            }`} data-testid="whatsapp-mode-banner">
                {isLive
                    ? <><CheckCircle size={16} weight="fill" /> Live — connected to Meta Cloud API.</>
                    : <><Clock size={16} weight="fill" /> Mock mode — logging to Mongo instead of hitting Meta. Set WHATSAPP_MOCK_MODE=false in backend/.env once Meta credentials are configured to go live.</>
                }
            </div>

            {lastPass && (
                <div className="rounded-xl border border-dashed border-border bg-card p-3 flex items-center gap-3 text-sm flex-wrap" data-testid="whatsapp-last-pass">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Last manual pass:</span>
                    <span className="tabular-nums"><span className="font-bold">{lastPass.candidates}</span> candidates</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="tabular-nums text-emerald-700"><span className="font-bold">{lastPass.sent}</span> sent</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="tabular-nums text-rose-700"><span className="font-bold">{lastPass.errors}</span> errors</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground text-xs">{fmtDateTime(lastPass.ran_at)}</span>
                </div>
            )}

            {/* iter100.268 — Meta template approval status. */}
            <TemplatesPanel templates={templates} loading={loading} onRefresh={load} />

            {/* Send log */}
            <AdminSectionShell
                eyebrow="Recent reminders"
                tone="teal"
                description="Every WhatsApp reminder we've sent from this environment (most recent first)."
                right={<span className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{items.length} shown</span>}
                testid="whatsapp-log-shell"
            >
                {items.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-8" data-testid="whatsapp-log-empty">
                        No reminders sent yet. Toggle opt-in on a patient with a lapsed wear log, then click &quot;Run smart trigger&quot;.
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="whatsapp-log-list">
                        {items.map((r, idx) => (
                            <div
                                key={r.wa_message_id || idx}
                                className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-center p-3 border border-border rounded-xl hover:border-primary/30 transition"
                                data-testid={`whatsapp-log-row-${idx}`}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <User size={14} className="text-primary shrink-0" weight="duotone" />
                                        <span className="font-semibold text-sm truncate">{r.patient_name}</span>
                                        <span className="text-[11px] text-muted-foreground font-mono">{r.phone}</span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        {r.template} · {r.days_since_last_log} days since last log
                                    </div>
                                </div>
                                <div className="text-[11px] text-muted-foreground tabular-nums">
                                    {fmtDateTime(r.sent_at)}
                                </div>
                                <div>
                                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-full border ${STATUS_STYLES[r.status] || "bg-secondary text-muted-foreground border-border"}`}>
                                        {r.status === "sent" && <CheckCircle size={10} weight="fill" />}
                                        {r.status === "error" && <XCircle size={10} weight="fill" />}
                                        {r.status}
                                    </span>
                                </div>
                                <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[140px]" title={r.wa_message_id}>
                                    {r.wa_message_id || "—"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </AdminSectionShell>

            <TestSendDialog open={testOpen} onClose={() => setTestOpen(false)} onSent={load} />
        </div>
    );
}


function StatTile({ label, value, Icon, tone = "primary", testid }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    return (
        <div className="flex items-start gap-3 rounded-xl bg-card border border-border p-3" data-testid={testid}>
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1 truncate">{value}</div>
            </div>
        </div>
    );
}


function TestSendDialog({ open, onClose, onSent }) {
    const [to, setTo] = useState("+91");
    const [name, setName] = useState("Test User");
    const [days, setDays] = useState(3);
    const [sending, setSending] = useState(false);

    const submit = async () => {
        if (!/^\+\d{7,15}$/.test(to)) {
            toast.error("Phone must be E.164 format (e.g. +919999999999)");
            return;
        }
        setSending(true);
        try {
            const r = await api.post("/admin/whatsapp/send-test", {
                to,
                template_name: "daily_wear_log_reminder",
                language_code: "en_US",
                body_params: [
                    { parameter_name: "patient_name", text: name || "there" },
                    { parameter_name: "days_since_last_log", text: String(days) },
                ],
            });
            const waId = r.data?.messages?.[0]?.id;
            toast.success(`Sent — ${waId}`);
            onSent?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="whatsapp-test-send-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PaperPlaneTilt size={18} weight="duotone" className="text-primary" /> Test-send a template
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label className="text-xs font-semibold">Recipient (E.164)</Label>
                        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+919999999999" className="h-10 mt-1.5 font-mono text-sm" data-testid="whatsapp-test-to" />
                        <p className="text-[10px] text-muted-foreground mt-1">In live mode, this must be a Meta-verified test recipient. In mock mode, any E.164 works.</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Patient name (template param)</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 mt-1.5" data-testid="whatsapp-test-name" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Days since last log (template param)</Label>
                        <Input type="number" value={days} onChange={(e) => setDays(Number(e.target.value) || 0)} className="h-10 mt-1.5" data-testid="whatsapp-test-days" />
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                    <Button onClick={submit} disabled={sending} data-testid="whatsapp-test-submit">
                        {sending ? "Sending…" : "Send"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* iter100.268 — Meta template approval status card. Reads from
   /admin/whatsapp/templates (which hits the Meta Graph API in live
   mode, or returns a synthetic APPROVED template in mock mode).
   Shows a coloured pill per template so the admin sees at a glance
   whether the smart-trigger loop will actually be able to send. */
function TemplatesPanel({ templates, loading, onRefresh }) {
    const [submitting, setSubmitting] = useState(false);

    const submitMissing = async () => {
        if (submitting) return;
        // Guardrail — this hits Meta's real API. Confirm before firing.
        const proceed = window.confirm(
            "Submit every locally-registered template that isn't already on Meta?\n\n" +
            "This makes real API calls to Meta. Each submission enters a 24–48h review queue and cannot be edited once submitted (only deleted + re-submitted)."
        );
        if (!proceed) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/whatsapp/templates/submit-all");
            const s = data?.summary || {};
            const parts = [];
            if (s.submitted) parts.push(`${s.submitted} submitted`);
            if (s.skipped)   parts.push(`${s.skipped} already on Meta`);
            if (s.errored)   parts.push(`${s.errored} errored`);
            toast.success(`Done — ${parts.join(" · ") || "no-op"}`, {
                description: s.errored
                    ? "Some templates failed. Check the console / server logs for details."
                    : "Meta will review new submissions within 24–48h. Refresh to see updated status.",
            });
            if (s.errored && Array.isArray(data?.results)) {
                const failed = data.results.filter((r) => r.status === "error");
                console.warn("[whatsapp] submit-all failures:", failed);
            }
            onRefresh();
        } catch (err) {
            toast.error("Bulk submit failed", {
                description: err?.response?.data?.detail || err?.message || "Meta unreachable",
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && (templates?.length ?? 0) === 0) {
        return (
            <div
                className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground"
                data-testid="whatsapp-templates-loading"
            >
                Loading Meta templates…
            </div>
        );
    }
    return (
        <AdminSectionShell
            eyebrow="Meta templates"
            tone="teal"
            description="Approval status pulled live from the WhatsApp Business Account. Pending templates cannot be sent until Meta approves them."
            right={
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={submitMissing}
                        disabled={submitting}
                        className="text-[10px] uppercase tracking-[0.14em] font-bold text-primary hover:underline flex items-center gap-1 disabled:opacity-40 disabled:cursor-wait"
                        data-testid="whatsapp-templates-submit-missing"
                        title="Push every locally-registered template that isn't already on Meta into Meta's review queue"
                    >
                        {submitting ? "Submitting…" : "Submit missing to Meta"}
                    </button>
                    <button
                        type="button"
                        onClick={onRefresh}
                        className="text-[10px] uppercase tracking-[0.14em] font-bold text-primary hover:underline flex items-center gap-1"
                        data-testid="whatsapp-templates-refresh"
                        title="Re-check with Meta right now"
                    >
                        <ArrowsClockwise size={11} weight="bold" /> Refresh
                    </button>
                </div>
            }
            testid="whatsapp-templates-shell"
        >
            {(templates?.length ?? 0) === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground" data-testid="whatsapp-templates-empty">
                    Could not fetch templates from Meta. This usually means the access token expired — regenerate it in the Meta Business Suite and update <span className="font-mono text-xs">WHATSAPP_ACCESS_TOKEN</span> in <span className="font-mono text-xs">backend/.env</span>.
                </div>
            ) : (
                <div className="space-y-2" data-testid="whatsapp-templates-list">
                    {templates.map((t) => (
                        <TemplateRow key={t.id || t.name} template={t} />
                    ))}
                </div>
            )}
        </AdminSectionShell>
    );
}

const TEMPLATE_STATUS_STYLES = {
    APPROVED:          "bg-emerald-100 text-emerald-800 border-emerald-200",
    PENDING:           "bg-amber-100 text-amber-800 border-amber-200",
    IN_APPEAL:         "bg-amber-100 text-amber-800 border-amber-200",
    PENDING_DELETION:  "bg-amber-100 text-amber-800 border-amber-200",
    REJECTED:          "bg-rose-100 text-rose-800 border-rose-200",
    DISABLED:          "bg-rose-100 text-rose-800 border-rose-200",
    PAUSED:            "bg-rose-100 text-rose-800 border-rose-200",
};

function TemplateRow({ template }) {
    const pill = TEMPLATE_STATUS_STYLES[template.status] || "bg-muted text-muted-foreground border-border";
    const isPending = template.status === "PENDING";
    const isRejected = ["REJECTED", "DISABLED", "PAUSED"].includes(template.status);
    return (
        <div
            className="flex items-start justify-between gap-3 rounded-xl border border-border bg-secondary/30 p-3"
            data-testid={`whatsapp-template-row-${template.name}`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold truncate">{template.name}</span>
                    <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${pill} uppercase tracking-wider`}
                        data-testid={`whatsapp-template-status-${template.name}`}
                    >
                        {template.status}
                    </span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                    <span>{template.category}</span>
                    <span>{template.language}</span>
                    {template.rejected_reason && template.rejected_reason !== "NONE" && (
                        <span className="text-rose-700 font-medium">
                            reason: {template.rejected_reason}
                        </span>
                    )}
                </div>
                {isPending && (
                    <div className="text-[11px] text-amber-800 mt-1.5">
                        Meta&apos;s typical review window is 24–48 hours. No action needed.
                    </div>
                )}
                {isRejected && (
                    <div className="text-[11px] text-rose-700 mt-1.5">
                        Fix the template in Meta Business Suite → WhatsApp Manager → Message Templates, then resubmit for review.
                    </div>
                )}
            </div>
        </div>
    );
}
